import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { 
  IoHome, 
  IoSearch, 
  IoSparkles,
  IoChevronBack,
  IoChevronForward,
  IoNotifications,
  IoSettings,
  IoPerson,
  IoDocumentText,
  IoCalendar,
  IoChatbubbles,
  IoBarChart,
  IoLibrary,
  IoAdd,
  IoCloudUpload
} from 'react-icons/io5';
import { IconType } from 'react-icons';
import { User } from '@/types';
import './Sidebar.css';

interface SidebarProps {
  user: User | null;
  onCollapseChange?: (collapsed: boolean) => void;
}

interface NavItem {
  icon: IconType;
  label: string;
  path: string;
  badge?: number | null;
  hasRoute?: boolean;
}

interface QuickAction {
  icon: IconType;
  label: string;
  onClick: () => void;
  badge?: number | null;
}


const Sidebar: React.FC<SidebarProps> = ({ user, onCollapseChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout: authLogout, startTour } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.email) {
      const username = user.email.split('@')[0];
      return username.charAt(0).toUpperCase() + username.slice(1);
    }
    return 'User';
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      setShowProfileDropdown(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setShowProfileDropdown(false);
      navigate('/login');
    }
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isCollapsed ? '5rem' : '18rem');
    onCollapseChange?.(isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleMobileClickOutside = (e: MouseEvent) => {
      if (window.innerWidth <= 834 && isMobileOpen) {
        const sidebar = document.querySelector('.sidebar');
        const toggleButton = document.querySelector('[aria-label="Toggle mobile menu"]');
        if (sidebar && !sidebar.contains(e.target as Node) && 
            toggleButton && !toggleButton.contains(e.target as Node)) {
          setIsMobileOpen(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleMobileClickOutside);
    return () => document.removeEventListener('mousedown', handleMobileClickOutside);
  }, [isMobileOpen]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const navItems: NavItem[] = [
    {
      icon: IoHome,
      label: 'Dashboard',
      path: '/',
      badge: null,
      hasRoute: true,
    },
    {
      icon: IoDocumentText,
      label: 'My applications',
      path: '/applications',
      badge: null,
      hasRoute: true,
    },
    {
      icon: IoSearch,
      label: 'College Search',
      path: '/college-list-builder',
      badge: null,
      hasRoute: true,
    },
    {
      icon: IoCalendar,
      label: 'Deadlines',
      path: '/deadlines',
      badge: null,
      hasRoute: true,
    },
    {
      icon: IoChatbubbles,
      label: 'Messages',
      path: '/messages',
      badge: null,
      hasRoute: true,
    },
    {
      icon: IoBarChart,
      label: 'Progress',
      path: '/progress',
      badge: null,
      hasRoute: true,
    },
    {
      icon: IoLibrary,
      label: 'Resources',
      path: '/resources',
      badge: null,
      hasRoute: true,
    },
  ];

  const quickActions: QuickAction[] = [
    {
      icon: IoAdd,
      label: 'New application',
      onClick: () => {/* TODO: Implement new application */},
      badge: null,
    },
    {
      icon: IoCloudUpload,
      label: 'Upload document',
      onClick: () => {/* TODO: Implement upload document */},
      badge: null,
    },
    {
      icon: IoSparkles,
      label: 'AI essay review',
      onClick: () => navigate('/essay-coach'),
      badge: null,
    },
  ];


  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/essay-coach') {
      return location.pathname === '/essay-coach' || location.pathname.startsWith('/analyze');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: NavItem) => {
    if (item.hasRoute && item.path !== '#') {
      navigate(item.path);
    }
    // For items without routes, do nothing (just show the item)
  };

  return (
    <>
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[999] md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      <button
        className="fixed top-4 left-4 z-[9999] md:hidden rounded-lg p-2 shadow-lg border cursor-pointer transition-colors"
        style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsMobileOpen(prev => !prev);
        }}
        aria-label="Toggle mobile menu"
      >
        {isMobileOpen ? (
          <svg className="w-6 h-6" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header">
        <a 
          className="sidebar-logo-link" 
          href="#" 
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
        >
          <div className="sidebar-logo">
            <img 
              src="/logo_1.png" 
              alt="APPLY.AI" 
              className="sidebar-logo-img"
            />
            {!isCollapsed && (
              <span className="sidebar-logo-text">APPLY.AI</span>
            )}
          </div>
        </a>
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-nav-list">
          {navItems.map((item, index) => {
            const IconComponent = item.icon;
            const active = item.hasRoute ? isActiveRoute(item.path) : false;
            return (
              <li key={index}>
                <a
                  className={`sidebar-nav-item ${active ? 'active' : ''} ${!item.hasRoute ? 'sidebar-nav-item-disabled' : ''}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item);
                  }}
                >
                  <IconComponent className="sidebar-nav-icon" />
                  {!isCollapsed && (
                    <>
                      <span className="sidebar-nav-label">{item.label}</span>
                      {item.badge !== null && (
                        <span className="sidebar-nav-badge">{item.badge}</span>
                      )}
                    </>
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        {!isCollapsed && (
          <div className="sidebar-quick-actions">
            <h3 className="sidebar-quick-actions-title">Quick Actions</h3>
            <ul className="sidebar-quick-actions-list">
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <li key={index}>
                    <button
                      className="sidebar-quick-action-item"
                      onClick={(e) => {
                        e.preventDefault();
                        action.onClick();
                      }}
                    >
                      <IconComponent className="sidebar-quick-action-icon" />
                      <span className="sidebar-quick-action-label">{action.label}</span>
                      {action.badge !== null && (
                        <span className="sidebar-nav-badge">{action.badge}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user-profile" ref={dropdownRef}>
          <button
            className="sidebar-user-avatar-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowProfileDropdown(!showProfileDropdown);
            }}
            type="button"
          >
            {user?.photoURL ? (
              <div className="sidebar-user-avatar-img-container">
                <img
                  src={user.photoURL}
                  alt={getUserDisplayName()}
                  className="sidebar-user-avatar-img"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const iconContainer = e.currentTarget.nextElementSibling as HTMLElement;
                    if (iconContainer) iconContainer.style.display = 'flex';
                  }}
                />
                <div className="sidebar-user-avatar-icon" style={{ display: 'none' }}>
                  <IoPerson className="sidebar-user-avatar-icon-svg" />
                </div>
              </div>
            ) : (
              <div className="sidebar-user-avatar-icon">
                <IoPerson className="sidebar-user-avatar-icon-svg" />
              </div>
            )}
          </button>
          {!isCollapsed && (
            <>
              <div className="sidebar-user-info">
                <p className="sidebar-user-name">{getUserDisplayName()}</p>
                <p className="sidebar-user-role">Class of 2026</p>
              </div>
              <div className="sidebar-user-actions">
                <button 
                  className="sidebar-user-action-button"
                  onClick={() => {/* TODO: Implement notifications */}}
                  title="Notifications"
                >
                  <IoNotifications className="sidebar-user-action-icon" />
                </button>
                <button
                  className="sidebar-user-action-button"
                  onClick={() => navigate('/settings')}
                  title="Settings"
                >
                  <IoSettings className="sidebar-user-action-icon" />
                </button>
              </div>
              {showProfileDropdown && (
                <div className="sidebar-profile-dropdown">
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowProfileDropdown(false);
                    }}
                    className="sidebar-dropdown-item"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowProfileDropdown(false);
                    }}
                    className="sidebar-dropdown-item"
                  >
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      navigate('/college-list-builder');
                      setTimeout(() => {
                        startTour();
                      }, 100);
                    }}
                    className="sidebar-dropdown-item"
                  >
                    Show Tour
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowProfileDropdown(false);
                      }}
                      className="sidebar-dropdown-item"
                    >
                      Admin Panel
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="sidebar-dropdown-item sidebar-dropdown-item-logout"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <button
        className="sidebar-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <IoChevronForward className="sidebar-toggle-icon" />
        ) : (
          <IoChevronBack className="sidebar-toggle-icon" />
        )}
      </button>
    </div>
    </>
  );
};

export default Sidebar;